import { rem } from 'polished'
import React, { memo, useCallback, useContext, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import useExperienceSearchLabels from 'components/Experiences/hooks/useExperienceSearchLabels'

import SearchLocationInput from 'components/Search/SearchForm/SearchLocationInput/SearchLocationInput'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import ExperiencesSearchDateSelectDropdown from './ExperiencesSearchDateSelectDropdown'

import { SearchMenuStates } from '../type'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { searchPaneInteractEventName } from 'components/Search/utils'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { EXPERIENCE_SEARCH_TYPES, EXPERIENCE_TYPEAHEAD_SEARCH_TYPES } from 'constants/experience'
import useRecentSearches from 'hooks/Search/useRecentSearches'
import useExperienceDateDisplayValue from 'components/Experiences/hooks/useExperienceDateDisplayValue'

const ControlsContainer = styled.div`
  position: relative;
  display: grid;
  gap: ${rem(12)};
  grid-template:
    "location-input"
    "date-input"
    "actions" auto / 1fr;

  ${mediaQueryUp.tablet} {
    grid-template: "location-input date-input actions" auto / 1fr 1fr ${rem(112)};
  }
`

const LocationInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: location-input;
`

const DateInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: date-input;
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: actions;
`

interface Props {
  onSearchButtonClick?: (e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
  disableSearch?: boolean,
}

function ExperiencesSearchInputs(props: Props) {
  const {
    onSearchButtonClick,
    disableSearch = false,
  } = props

  const { locationLabel, locationPlaceholderAnywhere, searchLabel, whenLabel, anytimePlaceholder } = useExperienceSearchLabels()
  const {
    activeMenu,
    checkinDate,
    checkoutDate,
    popularDestinations,
    disableRecentSearches,
  } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const recentSearches = useRecentSearches()

  const recentSearchItems = useMemo(() => {
    // Omits the rooms array from the recent search, we don't need it for experiences
    return recentSearches.map(recentSearch => ({
      searchItem: recentSearch.searchItem,
      dates: recentSearch.dates,
      recentSearchId: recentSearch.recentSearchId,
      dateAdded: recentSearch.dateAdded,
      rooms: [],
    }))
  }, [recentSearches])

  const setActiveMenu = useCallback((menu: SearchMenuStates) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu })
  }, [globalSearchDispatch])

  const closeMenu = useCallback(() => {
    setActiveMenu(SearchMenuStates.Closed)
  }, [setActiveMenu])

  const handleApply = useCallback(() => {
    onSearchButtonClick?.()
    closeMenu()
  }, [closeMenu, onSearchButtonClick])

  const onToggleMenu = useCallback((nextFilter?: SearchMenuStates) => {
    if (!nextFilter || activeMenu === nextFilter) {
      closeMenu()
    } else {
      setActiveMenu(nextFilter)
    }
  }, [activeMenu, closeMenu, setActiveMenu])

  const openLocationMenu = useCallback((e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation()
    document.dispatchEvent(new CustomEvent(searchPaneInteractEventName))
    onToggleMenu(SearchMenuStates.Locations)
  }, [onToggleMenu])

  const openDatePickerMenu = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    document.dispatchEvent(new CustomEvent(searchPaneInteractEventName))
    onToggleMenu(SearchMenuStates.Dates)
  }, [onToggleMenu])

  const inputRef = useRef<HTMLInputElement>(null)
  const controlsContainerRef = useRef<HTMLDivElement>(null)
  const dateTriggerRef = useRef<HTMLButtonElement>(null)

  const dateFieldDisplayValue = useExperienceDateDisplayValue(checkinDate, checkoutDate)

  return (
    <ControlsContainer ref={controlsContainerRef}>
      <LocationInputContainer>
        <SearchLocationInput
          data-testid="experiences-search-location-input"
          label={locationLabel}
          placeholder={locationPlaceholderAnywhere}
          searchTypes={EXPERIENCE_SEARCH_TYPES}
          placeTypes={EXPERIENCE_TYPEAHEAD_SEARCH_TYPES}
          variant="dropdown"
          onLocationToggle={openLocationMenu}
          searchVertical="experience"
          inputRef={inputRef}
          controlsContainerRef={controlsContainerRef}
          placeholderItems={popularDestinations}
          recentSearchItems={disableRecentSearches ? undefined : recentSearchItems}
          searchPath="/search/experiences"
          searchMapPath="/search/experiences/map"
        />
      </LocationInputContainer>
      <DateInputContainer>
        <SearchFormField
          ref={dateTriggerRef}
          label={whenLabel}
          placeholder={anytimePlaceholder}
          value={dateFieldDisplayValue}
          onClick={openDatePickerMenu}
        />
        <ExperiencesSearchDateSelectDropdown
          anchorRef={controlsContainerRef}
          triggerRef={dateTriggerRef}
          open={activeMenu === SearchMenuStates.Dates}
          onApply={handleApply}
          onDecline={closeMenu}
        />
      </DateInputContainer>
      <ActionsContainer>
        <TextButton kind="primary" type="submit" size="large" onClick={onSearchButtonClick} disabled={disableSearch}>{searchLabel}</TextButton>
      </ActionsContainer>
    </ControlsContainer>
  )
}

export default memo(ExperiencesSearchInputs)
